import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DURATION = 16; // secondes pour une montée complète (un peu plus lent)

// Un rond photo (visage) avec nom en arc. Fallback flamme si pas de photo.
const MemoryRound = ({ it, i, navigate }) => (
  <svg className="rm-svg" viewBox="0 0 100 100" data-testid={`memory-svg-${i}`}
       role="button" tabIndex={0} title={it.deceased_name}
       onClick={() => navigate(`/candle/${it.id}`)}
       onKeyDown={(e) => { if (e.key === "Enter") navigate(`/candle/${it.id}`); }}>
    <defs>
      <clipPath id={`rm-clip-${i}`}><circle cx="50" cy="44" r="33" /></clipPath>
      <clipPath id={`rm-flag-${i}`}><rect x="59" y="9" width="24" height="17" rx="2.6" /></clipPath>
      <path id={`rm-arc-${i}`} d="M 14.8 73.6 A 46 46 0 0 0 85.2 73.6" fill="none" />
      {it.flag_colors && it.flag_colors.length ? (
        <linearGradient id={`rm-ring-${i}`} x1="0" y1="0" x2="1" y2="1">
          {it.flag_colors.map((col, k) => (
            <stop key={k} offset={`${it.flag_colors.length === 1 ? 0 : (k / (it.flag_colors.length - 1)) * 100}%`} stopColor={col} />
          ))}
        </linearGradient>
      ) : null}
    </defs>
    {it.photo_url ? (
      <image href={`${BACKEND}${it.photo_url}`} x="17" y="11" width="66" height="66"
             clipPath={`url(#rm-clip-${i})`} preserveAspectRatio="xMidYMid slice" />
    ) : (
      <>
        <circle cx="50" cy="44" r="33" fill="rgba(18,18,22,0.95)" />
        <path d="M50 26 C 44 34, 43 40, 47 46 C 48 42, 50 41, 51 38 C 54 43, 58 46, 56 53 C 55 58, 50 61, 46 60 C 41 59, 39 53, 42 48 C 40 50, 39 54, 40 57 C 37 53, 37 46, 42 40 C 45 36, 49 32, 50 26 Z" fill="rgba(251,191,36,0.95)" />
      </>
    )}
    <circle cx="50" cy="44" r="33" fill="none" stroke={it.flag_colors && it.flag_colors.length ? `url(#rm-ring-${i})` : "rgba(212,175,55,0.9)"} strokeWidth={it.flag_colors && it.flag_colors.length ? 5 : 2.5} />
    {it.flag ? (
      <g className="rm-flag">
        <rect x="59" y="9" width="24" height="17" rx="2.6" fill="#0b0b0d" />
        <image href={it.flag} x="59" y="9" width="24" height="17" preserveAspectRatio="xMidYMid slice" clipPath={`url(#rm-flag-${i})`} />
        <rect x="59" y="9" width="24" height="17" rx="2.6" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="1.1" />
      </g>
    ) : null}
    <text className="rm-arc-text">
      <textPath href={`#rm-arc-${i}`} startOffset="50%" textAnchor="middle">{it.deceased_name}</textPath>
    </text>
  </svg>
);

// Bougie déplaçable (glisser-déposer) côté client connecté
const SortableMemory = ({ it, navigate, delay = 0 }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: it.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: "none",
    opacity: isDragging ? 0.35 : undefined,
    zIndex: isDragging ? 20 : undefined,
    "--d": `${delay}s`,
  };
  return (
    <figure ref={setNodeRef} style={style} className="side-memory"
            data-testid={`side-memory-${it.id}`} {...attributes} {...listeners}>
      <MemoryRound it={it} i={it.id} navigate={navigate} />
    </figure>
  );
};

// Ordonne une liste chronologique (ancien->récent) en ordre de rendu de grille
// (haut->bas) : les plus ANCIENS en bas (rangées complètes), les plus RÉCENTS en haut.
const toGridOrder = (chrono) => {
  const pairs = [];
  for (let i = 0; i < chrono.length; i += 2) pairs.push(chrono.slice(i, i + 2));
  pairs.reverse(); // la paire la plus récente en premier (en haut)
  return pairs.flat();
};

// Colonne droppable (gauche/droite), ancrée au bas de la bougie.
// items = ordre de rendu (haut->bas). Si nombre impair (>=3), on insère une cellule
// vide en tête pour que la flamme du haut se place à DROITE et garder la rangée du bas complète.
const DroppableColumn = ({ id, side, items, navigate, startDelay = 0 }) => {
  const { setNodeRef } = useDroppable({ id });
  const spacer = items.length >= 3 && items.length % 2 === 1;
  return (
    <SortableContext id={id} items={items.map((i) => i.id)} strategy={rectSortingStrategy}>
      <div ref={setNodeRef} className={`side-memories-col side-memories-col--${side}`}
           data-testid={`side-memories-${side}`}>
        {spacer && <span className="side-memory-spacer" aria-hidden="true" />}
        {items.map((it, i) => (
          <SortableMemory key={it.id} it={it} navigate={navigate} delay={startDelay + i * 0.1} />
        ))}
      </div>
    </SortableContext>
  );
};

// Disposition par défaut : 2 en bas à GAUCHE, 2 en bas à DROITE.
// Les nouvelles flammes (plus récentes) s'ajoutent AU-DESSUS, à DROITE, et ainsi de suite.
const buildColumns = (list) => {
  const hasSide = list.some((c) => c.home_side === "left" || c.home_side === "right");
  if (hasSide) {
    // ordre de rendu (grille remplie haut->bas) = display_order croissant
    const left = list.filter((c) => c.home_side === "left").sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    const right = list.filter((c) => c.home_side === "right").sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    // nouvelles bougies (sans côté) : au-dessus à droite -> en tête du tableau droite
    const rest = list.filter((c) => c.home_side !== "left" && c.home_side !== "right")
      .sort((a, b) => (b.death_date || "").localeCompare(a.death_date || "")); // décès le plus récent d'abord
    rest.forEach((c) => right.unshift(c));
    return { left, right };
  }
  // défaut : classé par DATE DE DÉCÈS (le plus récent en haut).
  // les 2 décès les plus anciens en bas à gauche, les 2 suivants en bas à droite,
  // le reste (décès plus récents) empilé au-dessus à droite (le dernier décès tout en haut).
  const chrono = [...list].sort((a, b) => (a.death_date || "").localeCompare(b.death_date || "")); // ancien -> récent
  const left = chrono.slice(0, 2);
  const right = chrono.slice(2);
  return { left: toGridOrder(left), right: toGridOrder(right) };
};

// Accueil :
// - CLIENT CONNECTÉ : SES bougies ACTIVES payées, de chaque côté (gauche/droite), déplaçables
//   directement par glisser-déposer (souris + tactile). Ancrées au bas de la bougie, 2 par 2.
// - VISITEUR : ronds des visages qui MONTENT (décédés du jour / mémoriaux récents).
export const RisingMemories = () => {
  const [items, setItems] = useState([]); // montent (visiteur)
  const [cols, setCols] = useState({ left: [], right: [] }); // client connecté
  const colsRef = useRef(cols);
  const hasOwn = useRef(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { colsRef.current = cols; }, [cols]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        if (user) {
          const res = await api.get("/me/candles");
          const mine = (res.data || [])
            .filter((c) => c.paid && !c.is_expired) // bougies payées ET actives (on retire les éteintes)
            .map((c) => ({
              id: c.id,
              deceased_name: c.deceased_name || "",
              photo_url: c.photo_base64 ? `/api/candles/${c.id}/photo` : null,
              flag: c.flag || null,
              country_name: c.country_name || null,
              home_side: c.home_side,
              display_order: c.display_order || 0,
              created_at: c.created_at,
              death_date: c.death_date || "",
            }));
          if (!active) return;
          hasOwn.current = mine.length > 0;
          setCols(buildColumns(mine));
          setItems([]);
          return;
        }
        const res = await api.get("/public/recent-candles?limit=20");
        if (!active) return;
        hasOwn.current = false;
        setCols({ left: [], right: [] });
        setItems(res.data?.items || []); // 20 derniers par date de décès, tout mélangé
      } catch (e) { /* silent */ }
    };
    load();
    const timer = setInterval(load, 45000);
    return () => { active = false; clearInterval(timer); };
  }, [user]);

  const findContainer = (id) => {
    if (id === "left" || id === "right") return id;
    const c = colsRef.current;
    if (c.left.some((i) => i.id === id)) return "left";
    if (c.right.some((i) => i.id === id)) return "right";
    return null;
  };

  const persist = async (c) => {
    const positions = [
      ...c.left.map((it, idx) => ({ id: it.id, side: "left", order: idx })),
      ...c.right.map((it, idx) => ({ id: it.id, side: "right", order: idx })),
    ];
    try { await api.put("/me/candles/layout", { positions }); } catch (e) { /* silent */ }
  };

  const handleDragOver = ({ active, over }) => {
    if (!over) return;
    const from = findContainer(active.id);
    const to = findContainer(over.id);
    if (!from || !to || from === to) return;
    setCols((prev) => {
      const fromItems = prev[from];
      const toItems = prev[to];
      const idx = fromItems.findIndex((i) => i.id === active.id);
      const item = fromItems[idx];
      if (!item) return prev;
      let newIndex;
      if (over.id === to) newIndex = toItems.length;
      else {
        const overIdx = toItems.findIndex((i) => i.id === over.id);
        newIndex = overIdx >= 0 ? overIdx : toItems.length;
      }
      return {
        ...prev,
        [from]: fromItems.filter((i) => i.id !== active.id),
        [to]: [...toItems.slice(0, newIndex), item, ...toItems.slice(newIndex)],
      };
    });
  };

  const handleDragEnd = ({ active, over }) => {
    if (over) {
      const from = findContainer(active.id);
      const to = findContainer(over.id);
      if (from && to && from === to && active.id !== over.id) {
        setCols((prev) => {
          const list = prev[from];
          const oldIndex = list.findIndex((i) => i.id === active.id);
          const newIndex = list.findIndex((i) => i.id === over.id);
          if (oldIndex < 0 || newIndex < 0) return prev;
          return { ...prev, [from]: arrayMove(list, oldIndex, newIndex) };
        });
      }
    }
    setTimeout(() => persist(colsRef.current), 0);
  };

  if (!items.length && !cols.left.length && !cols.right.length) return null;

  // Client connecté : glisser-déposer direct des bougies (gauche/droite, haut/bas)
  if (hasOwn.current) {
    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter}
                  onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
        <div className="side-memories" data-testid="side-memories">
          <DroppableColumn id="left" side="left" items={cols.left} navigate={navigate} startDelay={0.08} />
          <DroppableColumn id="right" side="right" items={cols.right} navigate={navigate} startDelay={0.16} />
        </div>
      </DndContext>
    );
  }

  // Visiteur : ronds qui montent
  return (
    <div className="rising-memories" data-testid="rising-memories" aria-hidden="true">
      {items.map((it, i) => {
        const delay = -((i * DURATION) / Math.max(items.length, 1)).toFixed(2);
        const isLeft = i % 2 === 0;
        const tier = Math.floor(i / 2) % 3;
        const startX = isLeft ? 30 - tier * 8 : 70 + tier * 8;
        const drift = (isLeft ? -1 : 1) * (12 + tier * 6);
        return (
          <figure key={it.id} className="rising-memory" data-testid={`rising-memory-${i}`}
            style={{ left: `${startX}%`, animationDelay: `${delay}s`, animationDuration: `${DURATION}s`, "--drift": `${drift}px` }}>
            <MemoryRound it={it} i={i} navigate={navigate} />
          </figure>
        );
      })}
    </div>
  );
};
